import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WORKFLOW_COMPONENTS } from '../../component-loader/definitions';
import { AutomationNodeComponent, AutomationNodeContentDirective } from '../automation-node/automation-node.component';
import { DisplayService } from './display.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { MultiBranchNodeData } from '../../../common/multi-node-branch-data';
import { MultiBranchNodeContentComponent } from '../../../common/components/multi-branch-node-content/multi-branch-node-content.component';
import { DynamicComponentDirective } from '../../../common/dynamic-component.directive';
import { AutomationErrorHandlerService } from '../../services';
import { Branch } from '../../component-loader/branch-options';
import { SideMenuTypes } from '../../automation-editor.service';
import { ChangeSideMenuContent } from '../../workflow-viewer-v2/workflow-viewer-v2.service';

export type NodeType = 'trigger' | 'task';

@Component({
  selector: 'automata-common-node',
  template: `
    <ng-container *ngIf="step; else addTriggerNode">
      <ng-container *ngIf="displayContent$ | async as displayContent; else loading">
        <ng-container *ngIf="!useMultiBranching">
          <automata-automation-node
            [type]="type"
            [icon]="icon"
            [iconColor]="iconColor"
            [step]="step"
            [precedingSteps]="precedingSteps"
            [filters]="filters"
            [automation]="automation"
            [enabled]="enabled"
            [selected]="selectedStepId === step.id"
            (goToStepClicked)="goToStepClicked.emit($event)"
            (contentClicked)="changeSideMenuContentHandler(); contentClicked.emit()"
            (click)="changeSideMenuContentHandler()"
          >
            <automata-automation-node-content [innerHTML]="displayContent"></automata-automation-node-content>
          </automata-automation-node>
        </ng-container>

        <ng-container *ngIf="useMultiBranching">
          <automata-automation-node
            [type]="type"
            [icon]="icon"
            [iconColor]="iconColor"
            [step]="step"
            [precedingSteps]="precedingSteps"
            [filters]="filters"
            [automation]="automation"
            [enabled]="enabled"
            [selected]="selectedStepId === step.id"
            (goToStepClicked)="goToStepClicked.emit($event)"
            (contentClicked)="changeSideMenuContentHandler(); contentClicked.emit()"
            (click)="changeSideMenuContentHandler()"
          >
            <automata-automation-node-content>
              <automata-multi-branch-node-content [branchData]="displayContent"></automata-multi-branch-node-content>
            </automata-automation-node-content>
          </automata-automation-node>
        </ng-container>
      </ng-container>
    </ng-container>

    <!-- This dynamicComponent is used mainly to load view data for filters at this moment. -->
    <div class="dynamic-hidden-content">
      <ng-template automataDynamicComponent></ng-template>
    </div>

    <ng-template #loading>
      <mat-card appearance="outlined">
        <div class="loading-card stencil-shimmer"></div>
      </mat-card>
    </ng-template>

    <ng-template #addTriggerNode>
      <automata-automation-node type="trigger">
        <automata-automation-node-content>
          {{ 'AUTOMATIONS.EDITOR.TRIGGERS.ADD' | translate }}
        </automata-automation-node-content>
      </automata-automation-node>
    </ng-template>
  `,
  styleUrls: ['./common-node-styles.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MultiBranchNodeContentComponent,
    DynamicComponentDirective,
    GalaxyPipesModule,
    AutomationNodeContentDirective,
    AutomationNodeComponent,
  ],
})
export class CommonNodeComponent implements OnInit, OnChanges {
  @ViewChild(DynamicComponentDirective, { static: true }) dynamicComponent: DynamicComponentDirective;
  @ViewChild(AutomationNodeComponent) card: AutomationNodeComponent;

  @Input() type: NodeType;
  @Input() step: WorkflowStepInterface | null;
  @Input() precedingSteps: string[];
  @Input() automation: AutomationInterface;
  @Input() branch: Branch;
  @Input() enabled = true;
  @Input() selectedStepId = '';

  @Output() goToStepClicked = new EventEmitter<string>();
  @Output() contentClicked = new EventEmitter<void>();
  @Output() changeSideMenuContent: EventEmitter<ChangeSideMenuContent> = new EventEmitter<ChangeSideMenuContent>();

  filters: WorkflowStepInterface[];

  icon: string;
  iconColor = '';
  useMultiBranching = false;

  displayContent$$: ReplaySubject<string | MultiBranchNodeData[]> = new ReplaySubject<string>(1);
  displayContent$: Observable<string | MultiBranchNodeData[]> = this.displayContent$$.asObservable();

  constructor(
    private readonly actionService: DisplayService,
    private readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  // ngOnChanges can be removed when the old editor is removed. The new editor uses ngOnInit instead.
  ngOnChanges(changes: SimpleChanges): void {
    this.updateContent(changes);
  }

  ngOnInit(): void {
    if (!this.step) {
      return;
    }
    this.updateContent(undefined, true);
  }

  updateContent(changes?: SimpleChanges, forceRecalculation = false): void {
    let recalculateActionSubtitle = forceRecalculation;
    let type = this.type;
    let step = this.step;
    let automation = this.automation;
    if (changes?.type?.currentValue) {
      type = changes.type.currentValue;
      recalculateActionSubtitle = true;
    }
    if (changes?.step?.currentValue) {
      step = changes.step.currentValue;
      recalculateActionSubtitle = true;
    }
    if (changes?.automation?.currentValue) {
      automation = changes.automation.currentValue;
      recalculateActionSubtitle = true;
    }

    if (type === 'trigger') {
      this.filters = automation.triggerFilters;
    }

    const def = WORKFLOW_COMPONENTS[step?.taskDefinitionId];
    this.icon = def?.icon;
    this.iconColor = def?.iconColor;
    if (recalculateActionSubtitle) {
      if (def?.useMultiBranchingForNodeDisplay) {
        this.useMultiBranching = true;
        this.actionService
          .getMultiBranchNodeData(
            automation.namespace,
            step.taskDefinitionId,
            step,
            automation,
            !!step.waitForTriggerDefinitionId,
          )
          .pipe(take(1))
          .subscribe((multiBranchData) => {
            this.displayContent$$.next(multiBranchData);
          });
      } else {
        this.useMultiBranching = false;
        this.errorHandler.deleteValidationError(step?.id);
        this.actionService
          .getActionSubtitle(
            automation.namespace,
            step.taskDefinitionId,
            step,
            automation,
            !!step.waitForTriggerDefinitionId,
          )
          .pipe(take(1))
          .subscribe((subtitle) => {
            this.displayContent$$.next(subtitle);
          });
      }
    }
  }

  changeSideMenuContentHandler(): void {
    let menuType: SideMenuTypes = 'editStep';
    if (this.type === 'trigger') {
      if (!this.step?.id) {
        menuType = 'addTrigger';
      } else {
        menuType = 'editTrigger';
      }
    }
    this.changeSideMenuContent.emit({
      requestingId: this.step?.id || '',
      menuType: menuType,
      taskDefinitionId: this.step?.taskDefinitionId,
      branch: this.branch,
      precedingSteps: this.precedingSteps,
    });
  }
}
